import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Briefcase,
  Building2,
  CalendarRange,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Clock,
  ExternalLink,
  Plus,
  Save,
  ShieldCheck,
  User,
  Users,
} from 'lucide-react';
import AccordionSection from '../../../components/UI/AccordionSection';
import { CountBadge } from '../../../components/UI/CountBadge';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import FormSelect from '../../../components/UI/FormSelect';
import { InfoBadge } from '../../../components/UI/InfoBadge';
import PillSelect from '../../../components/UI/PillSelect';
import PillToggle from '../../../components/UI/PillToggle';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { useTabPersistence } from '../../../hooks/usePersistence';
import {
  useLabourMarketSurvey,
  useSaveLabourMarketSurvey,
} from '../../../hooks/useLabourMarketSurvey';

const VALID_TABS = ['overview', 'workforce', 'exceptions', 'vacancies', 'practices', 'validation'];

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const SOURCE_KIND_BADGE = {
  computed: { color: 'green', label: 'In the portal' },
  heuristic: { color: 'orange', label: 'Inferred' },
  wait: { color: 'blue', label: 'Waiting on source' },
  missing: { color: 'red', label: 'Not in the portal' },
  attestation: { color: 'slate', label: 'HR attests' },
};

const metricNeedsDataWork = (item) => (
  ['heuristic', 'missing', 'wait'].includes(item.source_kind)
  || (item.source_kind === 'computed' && (item.gap_count || 0) > 0)
);

const metricIsReady = (item) => item.status === 'completed';

const formatCoverage = (item) => {
  if (item.coverage_total == null) {
    return item.coverage_ready ? String(item.coverage_ready) : '—';
  }
  return `${formatNumber(item.coverage_ready)} of ${formatNumber(item.coverage_total)}`;
};

const formatMetricFigure = (item) => {
  if (item.result_value == null) return null;
  const unit = item.result_unit ? ` ${item.result_unit}` : '';
  return `${formatNumber(item.result_value)}${unit}`;
};

const formatNumber = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-SG').format(Number(value));
};

const QUARTER_CUTOFF_LABEL = {
  1: '31 Mar',
  2: '30 Jun',
  3: '30 Sep',
  4: '31 Dec',
};

const currentLmsPeriod = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    quarter: Math.floor(now.getMonth() / 3) + 1,
  };
};

const periodValue = (year, quarter) => `${year}-${quarter}`;

const periodLabel = (year, quarter) => (
  `Q${quarter} ${year} · ${QUARTER_CUTOFF_LABEL[quarter]}`
);

const buildPeriodOptions = (selectedYear, selectedQuarter) => {
  const now = currentLmsPeriod();
  const keys = [];
  let year = now.year;
  let quarter = now.quarter;
  for (let i = 0; i < 8; i += 1) {
    keys.push(periodValue(year, quarter));
    quarter -= 1;
    if (quarter < 1) {
      quarter = 4;
      year -= 1;
    }
  }

  const selected = periodValue(selectedYear, selectedQuarter);
  const selectedValid = (
    selectedYear >= 2020
    && selectedQuarter >= 1
    && selectedQuarter <= 4
  );
  if (selectedValid && !keys.includes(selected)) {
    keys.unshift(selected);
  }

  const byYear = new Map();
  keys.forEach((key) => {
    const [optionYear, optionQuarter] = key.split('-').map(Number);
    if (!byYear.has(optionYear)) byYear.set(optionYear, []);
    byYear.get(optionYear).push({
      value: key,
      label: periodLabel(optionYear, optionQuarter),
    });
  });

  const options = [];
  [...byYear.keys()].sort((a, b) => b - a).forEach((optionYear) => {
    options.push({ isGroupHeader: true, label: String(optionYear) });
    options.push(...byYear.get(optionYear));
  });
  return options;
};

const returnStatusKey = (status) => {
  if (status === 'ready') return 'completed';
  if (status === 'submitted') return 'completed';
  if (status === 'in_progress') return 'in_progress';
  return 'draft';
};

const StatCard = ({ label, value, sub, icon: Icon, tone = 'red' }) => {
  const tones = {
    red: { bg: 'bg-red-100', text: 'text-red-600' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  };
  const colors = tones[tone] || tones.red;
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-start">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${colors.text}`} />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
          {sub ? <p className="text-[11px] sm:text-xs text-gray-500 mt-1">{sub}</p> : null}
        </div>
      </div>
    </div>
  );
};

const boolToSelect = (value) => {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';
  return '';
};

const selectToBool = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const LabourMarketSurveyPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fallback = currentLmsPeriod();
  const year = Number(searchParams.get('year')) || fallback.year;
  const quarter = Number(searchParams.get('quarter')) || fallback.quarter;
  const [activeTab, setActiveTab] = useTabPersistence('lms', 'overview', VALID_TABS);

  const { data, isLoading, isError, refetch } = useLabourMarketSurvey(year, quarter);
  const saveMutation = useSaveLabourMarketSurvey(year, quarter);

  const periodOptions = buildPeriodOptions(year, quarter);

  const setPeriod = (value) => {
    const [nextYear, nextQuarter] = String(value).split('-').map(Number);
    if (!nextYear || !nextQuarter) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('year', String(nextYear));
      next.set('quarter', String(nextQuarter));
      return next;
    }, { replace: true });
  };

  const exceptionCount = (
    (data?.exceptions?.occupation?.length || 0)
    + (data?.exceptions?.education?.length || 0)
    + (data?.exceptions?.identity?.length || 0)
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ClipboardList, count: data?.readiness?.blocking },
    { id: 'workforce', label: 'Workforce', icon: Users },
    { id: 'exceptions', label: 'Exceptions', icon: AlertTriangle, count: exceptionCount, countTheme: 'orange' },
    { id: 'vacancies', label: 'Vacancies', icon: Briefcase },
    { id: 'practices', label: 'Practices', icon: Building2 },
    { id: 'validation', label: 'Validation', icon: ShieldCheck },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <Link
            to="/admin/reports"
            aria-label="Back to Reports"
            title="Back to Reports"
            className="icon-button-admin flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">Labour Market Survey</h1>
            <p className="text-sm text-gray-500 mt-1">
              Quarterly MOM return — compute what the portal knows, close the gaps, attest the rest.
            </p>
          </div>
        </div>
        <PillSelect
          options={periodOptions}
          value={periodValue(year, quarter)}
          onChange={setPeriod}
          icon={CalendarRange}
          iconColor="text-red-600"
          theme="admin"
          ariaLabel="LMS quarter"
          minWidth="min-w-[220px]"
        />
      </div>

      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 ? (
                <CountBadge
                  count={tab.count}
                  theme={tab.countTheme || 'admin'}
                  active={isActive}
                  size="sm"
                />
              ) : null}
            </button>
          );
        })}
      </div>

      {isLoading ? <WorkspaceSkeleton /> : null}

      {isError && !isLoading ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Workspace unavailable</h3>
          <p className="text-sm text-gray-500">The Labour Market Survey could not be generated.</p>
          <button type="button" className="btn btn-admin-primary mt-4" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      ) : null}

      {data && !isLoading ? (
        <>
          {activeTab === 'overview' && (
            <OverviewPanel data={data} onOpenTab={setActiveTab} />
          )}
          {activeTab === 'workforce' && (
            <WorkforcePanel data={data} saveMutation={saveMutation} />
          )}
          {activeTab === 'exceptions' && (
            <ExceptionsPanel data={data} saveMutation={saveMutation} />
          )}
          {activeTab === 'vacancies' && (
            <VacanciesPanel data={data} saveMutation={saveMutation} />
          )}
          {activeTab === 'practices' && (
            <PracticesPanel data={data} saveMutation={saveMutation} />
          )}
          {activeTab === 'validation' && (
            <ValidationPanel
              data={data}
              saveMutation={saveMutation}
              onOpenTab={setActiveTab}
            />
          )}
        </>
      ) : null}
    </div>
  );
};

const WorkspaceSkeleton = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((key) => (
        <div key={key} className="bg-white rounded-2xl shadow-sm border p-4">
          <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
          <div className="h-8 w-16 bg-gray-100 rounded mt-3 animate-pulse" />
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl shadow-sm border p-6">
      <div className="h-4 w-48 bg-gray-100 rounded animate-pulse mb-4" />
      <div className="space-y-3">
        {[0, 1, 2, 3].map((key) => (
          <div key={key} className="h-12 bg-gray-50 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  </div>
);

const OverviewPanel = ({ data, onOpenTab }) => {
  const [metricFilter, setMetricFilter] = useState('data_work');
  const [metricsOpen, setMetricsOpen] = useState(true);
  const period = data.period || {};
  const workforce = data.workforce || {};
  const readiness = data.readiness || {};
  const company = data.company || {};
  const metrics = data.metrics || {};
  const metricItems = metrics.items || [];
  const metricSummary = metrics.summary || {};
  const daysLabel = period.days_to_cutoff < 0
    ? 'Cutoff passed'
    : `${period.days_to_cutoff} day${period.days_to_cutoff === 1 ? '' : 's'} to cutoff`;

  const visibleMetrics = metricItems.filter((item) => {
    if (metricFilter === 'data_work') return metricNeedsDataWork(item);
    if (metricFilter === 'attest') return item.source_kind === 'attestation';
    if (metricFilter === 'ready') return metricIsReady(item);
    return true;
  });

  const metricSections = [];
  visibleMetrics.forEach((item) => {
    const last = metricSections[metricSections.length - 1];
    if (!last || last.id !== item.section) {
      metricSections.push({ id: item.section, label: item.section_label, items: [item] });
    } else {
      last.items.push(item);
    }
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={returnStatusKey(data.return_status)} />
        <InfoBadge
          color={period.is_preview ? 'orange' : 'green'}
          label={period.is_preview ? 'Preview' : 'Frozen snapshot'}
        />
        <InfoBadge color="blue" label={period.as_of_label} />
        {company.name ? <InfoBadge color="slate" label={company.name} /> : null}
        {company.uen ? <InfoBadge color="gray" label={`UEN ${company.uen}`} /> : null}
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Paid employees"
          value={formatNumber(workforce.paid_employees)}
          sub={`${formatNumber(workforce.residents)} residents · ${formatNumber(workforce.foreigners)} foreign`}
          icon={Users}
          tone="red"
        />
        <StatCard
          label="Need data work"
          value={formatNumber(metricSummary.need_data_work)}
          sub="Metrics the portal cannot honestly copy onto the MOM form yet"
          icon={AlertTriangle}
          tone={metricSummary.need_data_work ? 'orange' : 'emerald'}
        />
        <StatCard
          label="HR still attests"
          value={formatNumber(metricSummary.attest_open)}
          sub="Union, activity, wage rule, re-employment and EAP"
          icon={ClipboardCheck}
          tone={metricSummary.attest_open ? 'blue' : 'emerald'}
        />
        <StatCard
          label="Cutoff"
          value={period.cutoff_date}
          sub={daysLabel}
          icon={CalendarRange}
          tone="purple"
        />
      </div>

      <AccordionSection
        expanded={metricsOpen}
        onToggle={() => setMetricsOpen((open) => !open)}
        theme="admin"
        contentVariant="flush"
        icon={<BookOpen className="w-4 h-4" />}
        iconBg="bg-red-50"
        title={(
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className="truncate">What the return asks</span>
            {(metricSummary.need_data_work || 0) > 0 && (
              <CountBadge
                count={metricSummary.need_data_work}
                theme="orange"
                size="md"
                className="flex-shrink-0"
              />
            )}
          </span>
        )}
      >
        <div className="px-4 py-3 border-b border-gray-100 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <p className="text-sm text-gray-500 min-w-0 lg:max-w-2xl">
            Official LMS definition, then an honest standing against what we store.
            Education is the largest gap. Occupation, weekly hours and vacancy wages
            are the others that need data work before this is copy-ready.
          </p>
          <div className="shrink-0">
            <PillToggle
              value={metricFilter}
              onChange={setMetricFilter}
              theme="admin"
              variant="compact"
              layout="sectionHeader"
              ariaLabel="Filter metrics"
              options={[
                { id: 'data_work', label: 'Need data work', count: metricSummary.need_data_work || 0 },
                { id: 'attest', label: 'Attest', count: metricSummary.attest_open || 0 },
                { id: 'ready', label: 'Ready', count: metricSummary.ready || 0 },
                { id: 'all', label: 'All', count: metricSummary.total || 0 },
              ]}
            />
          </div>
        </div>
        {visibleMetrics.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            Nothing in this filter.
          </div>
        ) : (
          <>
            <div className="lg:hidden divide-y divide-gray-100">
              {metricSections.map((section) => (
                <div key={section.id}>
                  <div className="px-4 py-2 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {section.label}
                  </div>
                  <div className="divide-y divide-gray-100">
                    {section.items.map((item) => {
                      const sourceBadge = SOURCE_KIND_BADGE[item.source_kind] || SOURCE_KIND_BADGE.missing;
                      return (
                        <div key={item.id} className="p-4 space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 break-words">{item.label}</p>
                              <div className="mt-1">
                                <StatusBadge status={item.status} />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => onOpenTab(item.tab)}
                              className="text-sm font-medium text-red-600 hover:text-red-700 shrink-0"
                            >
                              Open
                            </button>
                          </div>
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">LMS definition</p>
                            <p className="text-sm text-gray-700 leading-relaxed mt-1">{item.definition}</p>
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <InfoBadge color={sourceBadge.color} label={sourceBadge.label} />
                              <p className="text-sm font-medium text-gray-900 tabular-nums text-right">
                                {formatMetricFigure(item) || `Coverage ${formatCoverage(item)}`}
                              </p>
                            </div>
                            {item.result_note ? (
                              <p className="text-xs text-gray-500 text-right">{item.result_note}</p>
                            ) : null}
                            <p className="text-sm text-gray-500 leading-relaxed mt-1.5">{item.portal_source}</p>
                          </div>
                          <div className="bg-gray-50 rounded-xl p-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">What we do next</p>
                            <p className="text-sm text-gray-700 leading-relaxed mt-1">{item.work}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden lg:block">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <colgroup>
                  <col className="w-[16%]" />
                  <col className="w-[26%]" />
                  <col className="w-[22%]" />
                  <col className="w-[12%]" />
                  <col className="w-[18%]" />
                  <col className="w-[6%]" />
                </colgroup>
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Metric</th>
                    <th className="table-header-cell">LMS definition</th>
                    <th className="table-header-cell">Portal standing</th>
                    <th className="table-header-cell text-right">Coverage</th>
                    <th className="table-header-cell">What we do next</th>
                    <th className="table-header-cell text-right"><span className="sr-only">Open</span></th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {metricSections.map((section) => (
                    <React.Fragment key={section.id}>
                      <tr>
                        <td
                          colSpan={6}
                          className="table-cell bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500"
                        >
                          {section.label}
                        </td>
                      </tr>
                      {section.items.map((item) => {
                        const sourceBadge = SOURCE_KIND_BADGE[item.source_kind] || SOURCE_KIND_BADGE.missing;
                        return (
                          <tr key={item.id} className="table-row">
                            <td className="table-cell whitespace-normal align-top min-w-0">
                              <p className="text-sm font-medium text-gray-900 break-words leading-snug">
                                {item.label}
                              </p>
                              <div className="mt-1">
                                <StatusBadge status={item.status} />
                              </div>
                            </td>
                            <td className="table-cell whitespace-normal align-top min-w-0">
                              <p className="text-sm text-gray-600 break-words leading-relaxed">
                                {item.definition}
                              </p>
                            </td>
                            <td className="table-cell whitespace-normal align-top min-w-0">
                              <InfoBadge color={sourceBadge.color} label={sourceBadge.label} />
                              <p className="text-sm text-gray-500 break-words leading-relaxed mt-1.5">
                                {item.portal_source}
                              </p>
                            </td>
                            <td className="table-cell whitespace-normal align-top text-right">
                              <p className="text-sm font-medium text-gray-900 tabular-nums">
                                {formatMetricFigure(item) || formatCoverage(item)}
                              </p>
                              {item.result_note ? (
                                <p className="text-xs text-gray-500 mt-1">{item.result_note}</p>
                              ) : null}
                              {item.gap_count > 0 && item.coverage_total != null && item.result_value == null ? (
                                <p className="text-xs text-orange-600 mt-1">
                                  {formatNumber(item.gap_count)} still open
                                </p>
                              ) : null}
                            </td>
                            <td className="table-cell whitespace-normal align-top min-w-0">
                              <p className="text-sm text-gray-600 break-words leading-relaxed">
                                {item.work}
                              </p>
                            </td>
                            <td className="table-cell align-top text-right">
                              <button
                                type="button"
                                onClick={() => onOpenTab(item.tab)}
                                className="text-sm font-medium text-red-600 hover:text-red-700"
                              >
                                Open
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </AccordionSection>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Can we submit this quarter?</h2>
          <p className="text-sm text-gray-500 mt-1">
            Process ticks after the definitions above. Computed tables live in Workforce.
            Gaps that need a person sit in Exceptions. Vacancies and Practices are attestations.
          </p>
        </div>
        <ul className="divide-y divide-gray-100">
          {(readiness.items || []).map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => onOpenTab(item.tab)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{item.label}</p>
                    <StatusBadge status={item.status} />
                    {item.count > 0 && item.status !== 'completed' ? (
                      <CountBadge count={item.count} theme={item.blocking ? 'orange' : 'neutral'} />
                    ) : null}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{item.hint}</p>
                </div>
                <span className="text-xs font-medium text-red-600 flex-shrink-0">Open</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const WorkforcePanel = ({ data, saveMutation }) => {
  const workforce = data.workforce || {};
  const groups = Object.entries(workforce.by_occupation || {});
  const educationBands = data.catalogues?.education_bands || [];
  const reviewed = Boolean(data.review_flags?.workforce_reviewed);
  const payroll = data.payroll || {};

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          These are the tables you copy onto the MOM form. Unmapped people are excluded until you classify them.
        </p>
        <button
          type="button"
          className="btn btn-admin-primary"
          disabled={saveMutation.isPending || reviewed}
          onClick={() => saveMutation.mutate({ review_flags: { workforce_reviewed: true } })}
        >
          <CheckCircle2 className="w-4 h-4" />
          {reviewed ? 'Tables reviewed' : 'Mark tables reviewed'}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard
          label="Full-time"
          value={formatNumber(workforce.full_time)}
          sub={`${formatNumber(workforce.paid_employees)} paid employees in scope`}
          icon={Users}
          tone="red"
        />
        <StatCard
          label="Part-time"
          value={formatNumber(workforce.part_time)}
          sub="Employment type on the snapshot date"
          icon={User}
          tone="purple"
        />
        <StatCard
          label={payroll.ot_available ? 'Paid overtime' : 'OT hours'}
          value={formatNumber(payroll.ot_hours)}
          sub={
            payroll.ot_available
              ? 'Approved payroll for the quarter-end month'
              : payroll.through_date
                ? `Attendance through ${payroll.through_date}. Payroll is not approved.`
                : 'Quarter-end payroll has not been posted'
          }
          icon={Clock}
          tone={payroll.ot_available ? 'emerald' : 'orange'}
        />
        <StatCard
          label="Aged 60+"
          value={formatNumber(workforce.age_60_plus)}
          sub={workforce.age_unknown
            ? `${formatNumber(workforce.age_unknown)} missing a date of birth`
            : 'For the senior-worker questions'}
          icon={CalendarRange}
          tone="blue"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Paid employees by occupation</h2>
          <p className="text-sm text-gray-500 mt-1">
            Standard hours are a usual week, with meal breaks already excluded.
            {' '}
            Company total {formatNumber(workforce.standard_hours)} hours.
          </p>
        </div>
        <div className="md:hidden divide-y divide-gray-100">
          {groups.map(([key, row]) => (
            <div key={key} className="p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-sm font-semibold text-gray-900">{row.label}</p>
                <p className="text-lg font-semibold tabular-nums text-gray-900">
                  {formatNumber(row.total)}
                </p>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  ['Full-time', row.full_time],
                  ['Part-time', row.part_time],
                  ['Resident', row.resident],
                  ['Foreign', row.foreign],
                  ['Male', row.male],
                  ['Female', row.female],
                  [payroll.ot_available ? 'Paid OT hours' : 'OT hours', row.overtime_hours],
                  ['Standard hours', row.standard_hours],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-2">
                    <dt className="text-xs text-gray-500">{label}</dt>
                    <dd className="text-sm font-medium tabular-nums text-gray-900">
                      {formatNumber(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-compact">Occupation group</th>
                <th className="table-header-cell-compact text-right">Total</th>
                <th className="table-header-cell-compact text-right">Full-time</th>
                <th className="table-header-cell-compact text-right">Part-time</th>
                <th className="table-header-cell-compact text-right">Resident</th>
                <th className="table-header-cell-compact text-right">Foreign</th>
                <th className="table-header-cell-compact text-right">Male</th>
                <th className="table-header-cell-compact text-right">Female</th>
                <th className="table-header-cell-compact text-right">
                  {payroll.ot_available ? 'Paid OT hours' : 'OT hours'}
                </th>
                <th className="table-header-cell-compact text-right">Standard hours</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {groups.map(([key, row]) => (
                <tr key={key} className="table-row-dense">
                  <td className="table-cell-compact font-medium text-gray-900 whitespace-nowrap">{row.label}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.total)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.full_time)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.part_time)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.resident)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.foreign)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.male)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.female)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.overtime_hours)}</td>
                  <td className="table-cell-compact text-right tabular-nums">{formatNumber(row.standard_hours)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-row-dense border-t border-gray-200">
                <td className="table-cell-compact font-semibold text-gray-900">Total</td>
                <td className="table-cell-compact text-right tabular-nums font-semibold">
                  {formatNumber(workforce.paid_employees)}
                </td>
                <td className="table-cell-compact" colSpan={6} />
                <td className="table-cell-compact text-right tabular-nums font-semibold">
                  {formatNumber(workforce.overtime_hours)}
                </td>
                <td className="table-cell-compact text-right tabular-nums font-semibold">
                  {formatNumber(workforce.standard_hours)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Residents by highest completed education</h2>
          <p className="text-sm text-gray-500 mt-1">
            One band per resident — the highest completed attainment, not every certificate on file.
          </p>
        </div>
        <div className="md:hidden divide-y divide-gray-100">
          {groups.map(([key, row]) => (
            <div key={key} className="p-4">
              <p className="text-sm font-semibold text-gray-900 mb-3">{row.label}</p>
              <dl className="space-y-2.5">
                {educationBands.map((band) => (
                  <div key={band.value} className="flex items-center justify-between gap-3">
                    <dt className="text-xs text-gray-500">{band.label}</dt>
                    <dd className="text-sm font-medium tabular-nums text-gray-900">
                      {formatNumber(row.education?.[band.value] || 0)}
                    </dd>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                  <dt className="text-xs font-medium text-orange-700">Unmapped</dt>
                  <dd className="text-sm font-semibold tabular-nums text-orange-700">
                    {formatNumber(row.education_unmapped || 0)}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-compact">Occupation group</th>
                {educationBands.map((band) => (
                  <th key={band.value} className="table-header-cell-compact text-right whitespace-nowrap">{band.label}</th>
                ))}
                <th className="table-header-cell-compact text-right whitespace-nowrap">Unmapped</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {groups.map(([key, row]) => (
                <tr key={key} className="table-row-dense">
                  <td className="table-cell-compact font-medium text-gray-900 whitespace-nowrap">{row.label}</td>
                  {educationBands.map((band) => (
                    <td key={band.value} className="table-cell-compact text-right tabular-nums">
                      {formatNumber(row.education?.[band.value] || 0)}
                    </td>
                  ))}
                  <td className="table-cell-compact text-right tabular-nums text-orange-700">
                    {formatNumber(row.education_unmapped || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ExceptionsPanel = ({ data, saveMutation }) => {
  const [queue, setQueue] = useState('education');
  const [search, setSearch] = useState('');
  const occupationOptions = data.catalogues?.occupation_groups || [];
  const educationOptions = data.catalogues?.education_bands || [];
  const queues = [
    { id: 'occupation', label: 'Occupation', items: data.exceptions?.occupation || [] },
    { id: 'education', label: 'Education', items: data.exceptions?.education || [] },
    { id: 'identity', label: 'Identity', items: data.exceptions?.identity || [] },
  ];
  const active = queues.find((item) => item.id === queue) || queues[0];
  const filtered = active.items.filter((row) => {
    const haystack = `${row.name} ${row.staff_id} ${row.designation} ${row.detail}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });
  const educationRows = data.exceptions?.education || [];
  const educationSuggested = educationRows.filter((row) => row.suggested_band).length;
  const educationReady = Math.max(
    Number(data.workforce?.residents || 0) - educationRows.length,
    0,
  );

  const classify = (row, field, value) => {
    if (!value) return;
    if (field === 'occupation') {
      saveMutation.mutate({ occupation_overrides: { [row.staff_id]: value } });
    } else {
      saveMutation.mutate({
        education_attainments: {
          [row.staff_id]: {
            band: value,
            evidence_education_id: value === row.suggested_band
              ? row.evidence_education_id || null
              : null,
          },
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {queues.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setQueue(item.id)}
              className={`tab-btn-admin ${queue === item.id ? 'active' : ''}`}
            >
              {item.label}
              <CountBadge
                count={item.items.length}
                theme={item.items.length ? 'orange' : 'success'}
                active={queue === item.id}
              />
            </button>
          ))}
        </div>
        <div className="md:ml-auto w-full md:w-72">
          <DebouncedSearchInput
            value={search}
            onSearch={setSearch}
            placeholder="Search name, ID or title"
            theme="admin"
          />
        </div>
      </div>

      <p className="text-sm text-gray-500">
        {queue === 'occupation' && "Classify for this quarter's return. This does not rewrite the employee's designation."}
        {queue === 'education' && (
          'The portal compares every completed qualification and uses the highest LMS category. Review only ambiguous records or later qualifications that may change an HR-confirmed result.'
        )}
        {queue === 'identity' && 'These fields live on the employee profile. Open the record, fix the source, then come back.'}
      </p>

      {queue === 'education' ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard
            label="Ready"
            value={`${formatNumber(educationReady)} / ${formatNumber(data.workforce?.residents || 0)}`}
            sub="Derived from completed qualifications or resolved by HR"
            icon={CheckCircle2}
            tone="emerald"
          />
          <StatCard
            label="Ready to review"
            value={formatNumber(educationSuggested)}
            sub="A likely category exists, but another completed record remains ambiguous"
            icon={BookOpen}
            tone="blue"
          />
          <StatCard
            label="Needs investigation"
            value={formatNumber(educationRows.length - educationSuggested)}
            sub="No reliable suggestion from the education records"
            icon={AlertTriangle}
            tone="orange"
          />
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <ClipboardCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {active.items.length === 0 ? 'This queue is clear' : 'No matching people'}
          </h3>
          <p className="text-sm text-gray-500">
            {active.items.length === 0
              ? 'Nothing left to classify in this queue.'
              : 'Try a different name, staff ID or job title.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => (
            <div key={`${row.reason}-${row.staff_id}`} className="bg-white rounded-2xl shadow-sm border p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                    <InfoBadge color="gray" label={row.staff_id} />
                    <StatusBadge
                      status={
                        row.suggested_band || row.severity === 'review'
                          ? 'verification_required'
                          : 'blocked'
                      }
                    />
                    {queue === 'education' && row.suggested_band ? (
                      <InfoBadge
                        color="orange"
                        label={`Suggested: ${
                          educationOptions.find((option) => option.value === row.suggested_band)?.label
                          || row.suggested_band
                        }`}
                      />
                    ) : null}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {row.designation || 'No designation'}
                    {row.department ? ` · ${row.department}` : ''}
                  </p>
                  <p className="text-sm text-gray-700 mt-1">{row.detail}</p>
                  {queue === 'education' && row.current_band ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Current HR-confirmed category:{' '}
                      <span className="font-medium text-gray-700">
                        {educationOptions.find((option) => option.value === row.current_band)?.label
                          || row.current_band}
                      </span>
                    </p>
                  ) : null}
                  {queue === 'education' && row.education_records?.length ? (
                    <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-700">
                          Completed qualifications considered
                        </p>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {row.education_records.map((record) => (
                          <div
                            key={record.id}
                            className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5"
                          >
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {record.qualification || 'Unnamed qualification'}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {record.institution || 'Institution not recorded'}
                                {record.end_year ? ` · ${record.end_year}` : ''}
                              </p>
                            </div>
                            <span
                              className={`text-xs font-medium flex-shrink-0 ${
                                record.band ? 'text-gray-700' : 'text-orange-700'
                              }`}
                            >
                              {record.band_label || 'Needs HR classification'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="w-full lg:w-80 flex-shrink-0 space-y-2">
                  {queue === 'occupation' ? (
                    <FormSelect
                      options={occupationOptions}
                      value=""
                      onChange={(value) => classify(row, 'occupation', value)}
                      placeholder="Assign occupation group"
                      theme="admin"
                    />
                  ) : null}
                  {queue === 'education' ? (
                    <>
                      <FormSelect
                        options={educationOptions}
                        value=""
                        onChange={(value) => classify(row, 'education', value)}
                        placeholder="Set highest completed attainment"
                        theme="admin"
                        disabled={saveMutation.isPending}
                      />
                      {row.suggested_band ? (
                        <button
                          type="button"
                          className="btn btn-admin-secondary w-full justify-center"
                          disabled={saveMutation.isPending}
                          onClick={() => classify(row, 'education', row.suggested_band)}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Confirm suggested level
                        </button>
                      ) : null}
                    </>
                  ) : null}
                  <Link
                    to={row.profile_url}
                    className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Open employee record
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const VacanciesPanel = ({ data, saveMutation }) => {
  const catalogues = data.catalogues?.occupation_groups || [];
  const [rows, setRows] = useState(data.vacancies?.rows || []);
  const [jobsOpen, setJobsOpen] = useState(true);
  const [title, setTitle] = useState('');
  const [group, setGroup] = useState('');
  const [count, setCount] = useState('1');
  const [minWage, setMinWage] = useState('');
  const [maxWage, setMaxWage] = useState('');
  const rowsRef = useRef(rows);

  useEffect(() => {
    setRows(data.vacancies?.rows || []);
  }, [data.vacancies?.rows]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const persist = (nextRows, extra = {}) => {
    saveMutation.mutate({ vacancies: nextRows, ...extra });
  };

  const replaceRows = (updater, extra = {}) => {
    setRows((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      persist(next, extra);
      return next;
    });
  };

  const addRow = (seed = {}) => {
    const nextTitle = (seed.title || title).trim();
    if (!nextTitle) return;
    replaceRows((current) => ([
      ...current,
      {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `vac-${Date.now()}`,
        title: nextTitle,
        occupation_group: seed.occupation_group || group || null,
        n_vacancies: Number(seed.n_vacancies || count || 1),
        min_wage: minWage === '' ? null : Number(minWage),
        max_wage: maxWage === '' ? null : Number(maxWage),
        source: seed.source || 'hr',
      },
    ]));
    setTitle('');
    setGroup('');
    setCount('1');
    setMinWage('');
    setMaxWage('');
  };

  const patchRow = (id, patch, shouldPersist = false) => {
    if (shouldPersist) {
      replaceRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
      return;
    }
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const suggested = (data.vacancies?.suggested_from_jobs || []).filter((job) => (
    !rows.some((row) => row.title === job.title)
  ));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Open jobs are a prompt only — they have no wage band. Enter the vacancies you will report for this cutoff.
        </p>
        <button
          type="button"
          className="btn btn-admin-primary"
          disabled={saveMutation.isPending || data.vacancies?.reviewed}
          onClick={() => persist(rows, { review_flags: { vacancies_reviewed: true } })}
        >
          <CheckCircle2 className="w-4 h-4" />
          {data.vacancies?.reviewed ? 'Vacancies reviewed' : 'Mark vacancies reviewed'}
        </button>
      </div>

      {suggested.length > 0 ? (
        <AccordionSection
          expanded={jobsOpen}
          onToggle={() => setJobsOpen((open) => !open)}
          theme="admin"
          contentVariant="padded"
          icon={<Briefcase className="w-4 h-4 text-red-600" />}
          iconBg="bg-red-100"
          title="Open jobs in the portal"
          trailing={<CountBadge count={suggested.length} theme="neutral" size="md" />}
        >
          <div className="space-y-2">
            {suggested.map((job) => (
              <div key={job.job_id} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{job.title}</p>
                  <p className="text-xs text-gray-500">
                    {job.suggested_group
                      ? catalogues.find((item) => item.value === job.suggested_group)?.label || job.suggested_group
                      : 'Occupation needs classification'}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-admin-secondary"
                  onClick={() => addRow({
                    title: job.title,
                    occupation_group: job.suggested_group,
                    n_vacancies: 1,
                    source: 'job',
                  })}
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            ))}
          </div>
        </AccordionSection>
      ) : null}

      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Add a vacancy</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <input
            className="input-admin xl:col-span-2"
            placeholder="Job title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <FormSelect
            options={catalogues}
            value={group}
            onChange={setGroup}
            placeholder="Occupation group"
            theme="admin"
          />
          <input
            className="input-admin"
            type="number"
            min="1"
            placeholder="Headcount"
            value={count}
            onChange={(event) => setCount(event.target.value)}
          />
          <button type="button" className="btn btn-admin-primary" onClick={() => addRow()}>
            <Plus className="w-4 h-4" /> Add vacancy
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            className="input-admin"
            type="number"
            min="0"
            placeholder="Min monthly wage (optional)"
            value={minWage}
            onChange={(event) => setMinWage(event.target.value)}
          />
          <input
            className="input-admin"
            type="number"
            min="0"
            placeholder="Max monthly wage (optional)"
            value={maxWage}
            onChange={(event) => setMaxWage(event.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="md:hidden">
          {rows.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No vacancies entered for this return</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {rows.map((row) => (
                <div key={row.id} className="p-4 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-gray-900">{row.title}</p>
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-700 shrink-0"
                      onClick={() => replaceRows((current) => current.filter((item) => item.id !== row.id))}
                    >
                      Remove
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Occupation
                    </label>
                    <FormSelect
                      options={catalogues}
                      value={row.occupation_group || ''}
                      onChange={(value) => patchRow(row.id, { occupation_group: value }, true)}
                      placeholder="Classify"
                      theme="admin"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Vacancies
                      </label>
                      <input
                        className="input-admin text-right"
                        type="number"
                        min="0"
                        value={row.n_vacancies}
                        onChange={(event) => patchRow(row.id, { n_vacancies: Number(event.target.value) })}
                        onBlur={() => persist(rowsRef.current)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Min wage
                      </label>
                      <input
                        className="input-admin text-right"
                        type="number"
                        min="0"
                        value={row.min_wage ?? ''}
                        onChange={(event) => patchRow(row.id, { min_wage: event.target.value === '' ? null : Number(event.target.value) })}
                        onBlur={() => persist(rowsRef.current)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Max wage
                      </label>
                      <input
                        className="input-admin text-right"
                        type="number"
                        min="0"
                        value={row.max_wage ?? ''}
                        onChange={(event) => patchRow(row.id, { max_wage: event.target.value === '' ? null : Number(event.target.value) })}
                        onBlur={() => persist(rowsRef.current)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell-compact">Job title</th>
                <th className="table-header-cell-compact">Occupation</th>
                <th className="table-header-cell-compact text-right">Vacancies</th>
                <th className="table-header-cell-compact text-right">Min wage</th>
                <th className="table-header-cell-compact text-right">Max wage</th>
                <th className="table-header-cell-compact" />
              </tr>
            </thead>
            <tbody className="table-body">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No vacancies entered for this return</p>
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="table-row-dense">
                  <td className="table-cell-compact">{row.title}</td>
                  <td className="table-cell-compact min-w-[220px]">
                    <FormSelect
                      options={catalogues}
                      value={row.occupation_group || ''}
                      onChange={(value) => patchRow(row.id, { occupation_group: value }, true)}
                      placeholder="Classify"
                      theme="admin"
                    />
                  </td>
                  <td className="table-cell-compact text-right">
                    <input
                      className="input-admin text-right"
                      type="number"
                      min="0"
                      value={row.n_vacancies}
                      onChange={(event) => patchRow(row.id, { n_vacancies: Number(event.target.value) })}
                      onBlur={() => persist(rowsRef.current)}
                    />
                  </td>
                  <td className="table-cell-compact text-right">
                    <input
                      className="input-admin text-right"
                      type="number"
                      min="0"
                      value={row.min_wage ?? ''}
                      onChange={(event) => patchRow(row.id, { min_wage: event.target.value === '' ? null : Number(event.target.value) })}
                      onBlur={() => persist(rowsRef.current)}
                    />
                  </td>
                  <td className="table-cell-compact text-right">
                    <input
                      className="input-admin text-right"
                      type="number"
                      min="0"
                      value={row.max_wage ?? ''}
                      onChange={(event) => patchRow(row.id, { max_wage: event.target.value === '' ? null : Number(event.target.value) })}
                      onBlur={() => persist(rowsRef.current)}
                    />
                  </td>
                  <td className="table-cell-compact text-right">
                    <button
                      type="button"
                      className="text-sm font-medium text-red-600 hover:text-red-700"
                      onClick={() => replaceRows((current) => current.filter((item) => item.id !== row.id))}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PracticesPanel = ({ data, saveMutation }) => {
  const saved = data.practices || {};
  const [form, setForm] = useState({
    main_activity: saved.main_activity || '',
    unionised: boolToSelect(saved.unionised),
    has_seniority_wage: boolToSelect(saved.has_seniority_wage),
    offers_reemployment: boolToSelect(saved.offers_reemployment),
    has_eap: boolToSelect(saved.has_eap),
    has_age_friendly_practices: boolToSelect(saved.has_age_friendly_practices),
    notes: saved.notes || '',
  });

  useEffect(() => {
    const next = data.practices || {};
    setForm({
      main_activity: next.main_activity || '',
      unionised: boolToSelect(next.unionised),
      has_seniority_wage: boolToSelect(next.has_seniority_wage),
      offers_reemployment: boolToSelect(next.offers_reemployment),
      has_eap: boolToSelect(next.has_eap),
      has_age_friendly_practices: boolToSelect(next.has_age_friendly_practices),
      notes: next.notes || '',
    });
  }, [data.practices]);

  const save = () => {
    saveMutation.mutate({
      practices: {
        main_activity: form.main_activity.trim(),
        unionised: selectToBool(form.unionised),
        has_seniority_wage: selectToBool(form.has_seniority_wage),
        offers_reemployment: selectToBool(form.offers_reemployment),
        has_eap: selectToBool(form.has_eap),
        has_age_friendly_practices: selectToBool(form.has_age_friendly_practices),
        notes: form.notes.trim(),
      },
    });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Establishment particulars</h2>
          <p className="text-sm text-gray-500 mt-1">
            The portal does not store these as operational fields. Answer them here as attestations for this return.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lms-main-activity">
            Main activity
          </label>
          <input
            id="lms-main-activity"
            className="input-admin"
            value={form.main_activity}
            onChange={(event) => setForm((current) => ({ ...current, main_activity: event.target.value }))}
            placeholder="e.g. General building construction"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Unionised establishment</p>
            <FormSelect
              options={YES_NO_OPTIONS}
              value={form.unionised}
              onChange={(value) => setForm((current) => ({ ...current, unionised: value }))}
              placeholder="Select"
              theme="admin"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Seniority-based wage system</p>
            <FormSelect
              options={YES_NO_OPTIONS}
              value={form.has_seniority_wage}
              onChange={(value) => setForm((current) => ({ ...current, has_seniority_wage: value }))}
              placeholder="Select"
              theme="admin"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Offers re-employment</p>
            <FormSelect
              options={YES_NO_OPTIONS}
              value={form.offers_reemployment}
              onChange={(value) => setForm((current) => ({ ...current, offers_reemployment: value }))}
              placeholder="Select"
              theme="admin"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Employee Assistance Programme / similar</p>
            <FormSelect
              options={YES_NO_OPTIONS}
              value={form.has_eap}
              onChange={(value) => setForm((current) => ({ ...current, has_eap: value }))}
              placeholder="Select"
              theme="admin"
            />
          </div>
          <div className="md:col-span-2">
            <p className="text-sm font-medium text-gray-700 mb-1">Age-friendly workplace practices</p>
            <FormSelect
              options={YES_NO_OPTIONS}
              value={form.has_age_friendly_practices}
              onChange={(value) => setForm((current) => ({ ...current, has_age_friendly_practices: value }))}
              placeholder="Select"
              theme="admin"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lms-notes">
            Notes for this return
          </label>
          <textarea
            id="lms-notes"
            className="textarea-admin"
            rows={4}
            value={form.notes}
            onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
            placeholder="Anything you will need when filling the remaining questionnaire items."
          />
        </div>
        <div className="flex justify-end">
          <button type="button" className="btn btn-admin-primary" onClick={save} disabled={saveMutation.isPending}>
            <Save className="w-4 h-4" /> Save practices
          </button>
        </div>
      </div>
    </div>
  );
};

const ValidationPanel = ({ data, saveMutation, onOpenTab }) => {
  const readiness = data.readiness || {};
  const declared = Boolean(data.declaration?.confirmed);
  const canDeclare = Boolean(readiness.can_declare) && !declared;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="Blocking" value={formatNumber(readiness.blocking)} icon={AlertTriangle} tone="orange" />
        <StatCard label="Review remaining" value={formatNumber(readiness.review)} icon={ClipboardList} tone="blue" />
        <StatCard label="Complete" value={formatNumber(readiness.ready)} icon={CheckCircle2} tone="emerald" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <ul className="divide-y divide-gray-100">
          {(readiness.items || []).map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{item.label}</p>
                  <StatusBadge status={item.status} />
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{item.hint}</p>
              </div>
              <button
                type="button"
                className="text-sm font-medium text-red-600"
                onClick={() => onOpenTab(item.tab)}
              >
                Go to {item.tab}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-red-600" />
          <h2 className="text-sm font-semibold text-gray-900">Declaration</h2>
        </div>
        <p className="text-sm text-gray-500">
          This does not submit to MOM. It records that HR has reviewed the computed tables
          and attestations and is ready to copy them onto the official form.
        </p>
        {declared ? (
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status="completed" label="Declared" />
            <InfoBadge color="green" label={data.declaration?.confirmed_at || 'Confirmed'} />
            <button
              type="button"
              className="text-sm font-medium text-red-600"
              onClick={() => saveMutation.mutate({ declaration: { confirmed: false } })}
            >
              Unlock declaration
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn-admin-primary"
            disabled={!canDeclare || saveMutation.isPending}
            onClick={() => saveMutation.mutate({ declaration: { confirmed: true } })}
          >
            <ClipboardCheck className="w-4 h-4" />
            Declare this return is ready to copy
          </button>
        )}
        {!canDeclare && !declared ? (
          <p className="text-sm text-gray-500">
            Clear every blocking item before declaring. Preview overtime and vacancy ticks can wait, but they should still be reviewed.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default LabourMarketSurveyPage;
